import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import {
  closeRing,
  distance,
  fitRingToRing,
  isValidRing,
  polygonToSvgPath,
} from '../../geometry/polygons';
import type { Point, Ring } from '../../geometry/types';
import type { GameComponentProps } from '../core/types';
import { shapeupAccuracy, shapeupDelta } from './logic';
import { isShapeupPuzzle, type ShapeupPuzzle } from './types';

type ShapeupResult = { points: Ring; accuracy: number; deltaMs: number };
type ShapeupSavedState = { version: 1; result: ShapeupResult | null };

function readSavedState(value: unknown): ShapeupSavedState {
  if (!value || typeof value !== 'object') return { version: 1, result: null };
  const candidate = value as Partial<ShapeupSavedState>;
  const result = candidate.result;
  if (
    !result ||
    !Array.isArray(result.points) ||
    !Number.isInteger(result.accuracy) ||
    typeof result.deltaMs !== 'number'
  ) {
    return { version: 1, result: null };
  }
  return { version: 1, result };
}

function canvasPoint(canvas: HTMLCanvasElement, clientX: number, clientY: number): Point {
  const bounds = canvas.getBoundingClientRect();
  return {
    x: Math.max(0, Math.min(1, (clientX - bounds.left) / bounds.width)),
    y: Math.max(0, Math.min(1, (clientY - bounds.top) / bounds.height)),
  };
}

function drawRing(context: CanvasRenderingContext2D, points: Ring, size: number) {
  if (points.length === 0) return;
  context.beginPath();
  context.moveTo(points[0].x * size, points[0].y * size);
  for (const point of points.slice(1)) context.lineTo(point.x * size, point.y * size);
}

export function ShapeupGame({
  puzzle,
  savedState,
  disabled,
  reducedMotion,
  onStateChange,
  onComplete,
}: GameComponentProps) {
  if (!isShapeupPuzzle(puzzle.payload)) throw new Error('Shapeup received an invalid puzzle.');
  const shapeup = puzzle.payload as ShapeupPuzzle;
  const initial = useMemo(() => readSavedState(savedState), [savedState]);
  const [path, setPath] = useState<Ring>(initial.result?.points ?? []);
  const [result, setResult] = useState<ShapeupResult | null>(initial.result);
  const [status, setStatus] = useState(
    initial.result ? `${initial.result.accuracy}% accuracy` : 'Start outside the dotted circle',
  );
  const [keyboardCursor, setKeyboardCursor] = useState<Point>({ x: 0.5, y: 0.18 });
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pathRef = useRef<Ring>(path);
  const drawingRef = useRef(false);
  const pointerRef = useRef<number | null>(null);
  const completionSentRef = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext('2d');
    if (!context) return;
    const size = canvas.width;
    context.clearRect(0, 0, size, size);
    context.fillStyle = '#282a31';
    context.fillRect(0, 0, size, size);

    context.beginPath();
    context.arc(size / 2, size / 2, shapeup.noGoRadius * size, 0, Math.PI * 2);
    context.setLineDash([7, 7]);
    context.lineWidth = 2;
    context.strokeStyle = '#777983';
    context.stroke();
    context.setLineDash([]);
    context.fillStyle = '#9a9ca3';
    context.beginPath();
    context.arc(size / 2, size / 2, 4, 0, Math.PI * 2);
    context.fill();

    if (path.length > 0) {
      drawRing(context, result ? closeRing(path) : path, size);
      context.lineCap = 'round';
      context.lineJoin = 'round';
      context.lineWidth = result ? 3 : 7;
      context.strokeStyle = result ? '#ef916f' : '#f3f0e8';
      if (result) {
        context.fillStyle = 'rgba(239, 145, 111, 0.3)';
        context.fill('evenodd');
      }
      context.stroke();
    }
    if (result) {
      const fittedTarget = fitRingToRing(shapeup.target, path);
      drawRing(context, closeRing(fittedTarget), size);
      context.fillStyle = 'rgba(76, 166, 124, 0.3)';
      context.fill('evenodd');
      context.lineWidth = 3;
      context.strokeStyle = '#4ca67c';
      context.stroke();
    }
  }, [path, result, shapeup.noGoRadius, shapeup.target]);

  useEffect(() => {
    if (!result || completionSentRef.current) return;
    const handle = window.setTimeout(
      () => {
        completionSentRef.current = true;
        onComplete({
          kind: result.accuracy >= 90 ? 'solved' : 'failed',
          baseDeltaMs: result.deltaMs,
          message: `${result.accuracy}% accuracy`,
          metadata: { accuracy: result.accuracy, pointCount: result.points.length },
        });
      },
      reducedMotion ? 180 : 900,
    );
    return () => window.clearTimeout(handle);
  }, [onComplete, reducedMotion, result]);

  const finish = (rawPoints: Ring) => {
    if (result) return;
    const valid = isValidRing(rawPoints);
    const points = valid ? closeRing(rawPoints) : rawPoints.map((point) => ({ ...point }));
    const accuracy = valid ? shapeupAccuracy(shapeup.target, points) : 0;
    const deltaMs = shapeupDelta(accuracy);
    const nextResult = { points, accuracy, deltaMs };
    pathRef.current = points;
    setPath(points);
    setResult(nextResult);
    setStatus(valid ? `${accuracy}% accuracy` : 'Gesture too small · 0% accuracy');
    onStateChange({ version: 1, result: nextResult });
  };

  const start = (point: Point) => {
    if (disabled || result) return false;
    if (distance(point, { x: 0.5, y: 0.5 }) < shapeup.noGoRadius) {
      setStatus('Start outside the dotted circle');
      return false;
    }
    drawingRef.current = true;
    pathRef.current = [point];
    setPath([point]);
    setStatus('Keep holding and trace one continuous outline');
    return true;
  };

  const pointerDown = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (
      (event.button ?? 0) !== 0 ||
      !start(canvasPoint(event.currentTarget, event.clientX, event.clientY))
    )
      return;
    pointerRef.current = event.pointerId;
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const pointerMove = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current || pointerRef.current !== event.pointerId) return;
    const point = canvasPoint(event.currentTarget, event.clientX, event.clientY);
    if (distance(point, pathRef.current[pathRef.current.length - 1]) < 0.004) return;
    pathRef.current = [...pathRef.current, point];
    setPath(pathRef.current);
  };

  const pointerUp = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current || pointerRef.current !== event.pointerId) return;
    const point = canvasPoint(event.currentTarget, event.clientX, event.clientY);
    pathRef.current = [...pathRef.current, point];
    drawingRef.current = false;
    pointerRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    finish(pathRef.current);
  };

  const cancel = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    pointerRef.current = null;
    pathRef.current = [];
    setPath([]);
    setStatus('Gesture cancelled · try again');
  };

  const keyboard = (event: ReactKeyboardEvent<HTMLCanvasElement>) => {
    if (disabled || result) return;
    const movement: Record<string, Point> = {
      ArrowLeft: { x: -0.025, y: 0 },
      ArrowRight: { x: 0.025, y: 0 },
      ArrowUp: { x: 0, y: -0.025 },
      ArrowDown: { x: 0, y: 0.025 },
    };
    if (movement[event.key]) {
      event.preventDefault();
      const next = {
        x: Math.max(0, Math.min(1, keyboardCursor.x + movement[event.key].x)),
        y: Math.max(0, Math.min(1, keyboardCursor.y + movement[event.key].y)),
      };
      setKeyboardCursor(next);
      if (drawingRef.current) {
        pathRef.current = [...pathRef.current, next];
        setPath(pathRef.current);
      }
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (drawingRef.current) {
        drawingRef.current = false;
        finish(pathRef.current);
      } else {
        start(keyboardCursor);
      }
    } else if (event.key === 'Escape') cancel();
  };

  return (
    <section className="shapeup-game" aria-labelledby="shapeup-title">
      <div className="shapeup-target">
        <span>Trace this shape</span>
        <svg viewBox="0 0 1 1" role="img" aria-label={`${shapeup.targetName} target thumbnail`}>
          <path d={polygonToSvgPath({ outer: shapeup.target, holes: [] })} />
        </svg>
        <h1 id="shapeup-title">{shapeup.targetName}</h1>
      </div>
      <canvas
        ref={canvasRef}
        className="geometry-canvas shapeup-canvas"
        width={600}
        height={600}
        tabIndex={disabled || result ? -1 : 0}
        role="application"
        aria-label={`Trace the ${shapeup.targetName}. Start outside the center circle. Press Space to start or submit with keyboard arrows.`}
        aria-disabled={disabled || Boolean(result)}
        onPointerDown={pointerDown}
        onPointerMove={pointerMove}
        onPointerUp={pointerUp}
        onPointerCancel={cancel}
        onKeyDown={keyboard}
      />
      <p className="geometry-instruction">
        Hold and trace around the center · release to submit one attempt
      </p>
      <p className="geometry-status" role="status" aria-live="assertive">
        {status}
      </p>
    </section>
  );
}
